import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldAlert, Loader2, Trash2, CheckCircle2, Database, AlertTriangle,
} from "lucide-react";

const TXN_LABELS = {
  inquiries: "Inquiries",
  quotations: "Quotations",
  quotation_revisions: "Quotation Revisions",
  purchase_orders: "Purchase Orders",
  order_acceptances: "Order Acceptances",
  cylinders: "Cylinder Records",
  cylinder_status_history: "Cylinder Status History",
  delivery_challans: "Delivery Challans (Receiving)",
  notifications: "Notifications",
  import_history: "Import History",
  login_attempts: "Login Attempt Logs",
};

const PRESERVED_LABELS = {
  users: "User Accounts",
  customers: "Customer Master",
  plants: "Plant / Location Master",
  counters: "Numbering Configuration",
  company_settings: "Company Settings",
  import_templates: "Import Templates",
};

// Admin-clickable routes for the "Will be preserved" table. Rows whose key is
// not in this map render as plain text (non-clickable).
const PRESERVED_ROUTES = {
  users: "/admin/users",
  customers: "/customers",
  plants: "/plants",
  counters: "/admin/numbering",
};

export default function SystemMaintenance() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (user && !["admin", "super_admin"].includes(user.role)) {
      toast.error("Only Administrator can access System Maintenance");
      nav("/dashboard");
    }
  }, [user, nav]);

  async function loadPreview() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance/preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      const { data } = await api.get("/admin/maintenance/history");
      setHistory(data || []);
    } catch (err) {
      // History is best-effort — never block the page.
      // eslint-disable-next-line no-console
      console.warn("[SystemMaintenance] history load failed:", err?.response?.status, err?.message);
      setHistory([]);
    }
  }

  useEffect(() => {
    if (user && ["admin", "super_admin"].includes(user.role)) {
      loadPreview();
      loadHistory();
    }
    // eslint-disable-next-line
  }, [user]);

  async function handleClearTransactions() {
    if (confirmText !== "DELETE ALL") {
      toast.error('Please type "DELETE ALL" exactly to confirm');
      return;
    }
    setClearing(true);
    try {
      const { data } = await api.post("/admin/maintenance/clear-transactions", {
        confirm: "DELETE ALL",
      });
      setLastResult(data);
      setShowConfirm(false);
      setConfirmText("");
      toast.success(`Cleared ${data.total_deleted} transactional records`);
      await loadPreview();
      await loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to clear transactions");
    } finally {
      setClearing(false);
    }
  }

  const totalTxn = preview?.total_transactional_records ?? 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="system-maintenance-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            <ShieldAlert className="w-3.5 h-3.5 text-red-700" />
            Administration
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">System Maintenance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Permanently purge transactional data to reset the platform. Masters, users, and numbering configuration are preserved.
          </p>
        </div>
      </div>

      {/* Preview Card */}
      <Card className="p-6 border-red-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-red-700" />
            <h2 className="text-lg font-semibold text-slate-900">Clear Transaction Data</h2>
          </div>
          <Button
            data-testid="btn-refresh-preview"
            variant="outline"
            size="sm"
            onClick={loadPreview}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
              Will be DELETED ({totalTxn} records)
            </div>
            <div className="border border-red-100 rounded-lg divide-y divide-red-50 bg-red-50/30">
              {Object.entries(preview?.transactional_collections || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-slate-700">{TXN_LABELS[k] || k}</span>
                  <span className="font-mono font-semibold text-red-700" data-testid={`txn-count-${k}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
              Will be PRESERVED
            </div>
            <div className="border border-emerald-100 rounded-lg divide-y divide-emerald-50 bg-emerald-50/30">
              {Object.entries(preview?.preserved_collections || {}).map(([k, v]) => {
                const route = PRESERVED_ROUTES[k];
                const label = PRESERVED_LABELS[k] || k;
                const content = (
                  <>
                    <span className={route ? "text-slate-800 group-hover:underline" : "text-slate-700"}>{label}</span>
                    <span className="font-mono font-semibold text-emerald-700">{v}</span>
                  </>
                );
                return route ? (
                  <button
                    key={k}
                    data-testid={`preserved-open-${k}`}
                    onClick={() => nav(route)}
                    className="group w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-emerald-50 transition-colors text-left cursor-pointer"
                    title={`Open ${label}`}
                  >
                    {content}
                  </button>
                ) : (
                  <div key={k} className="flex items-center justify-between px-3 py-2 text-sm">
                    {content}
                  </div>
                );
              })}
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-600 italic">Master Data · Vendor / Material / Department / Machine / Process / Cylinder Type / Units</span>
                <span className="font-mono text-slate-400">—</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            data-testid="btn-open-clear-confirm"
            className="bg-red-700 hover:bg-red-800 text-white"
            disabled={totalTxn === 0}
            onClick={() => setShowConfirm(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Transaction Data
          </Button>
        </div>
      </Card>

      {/* Last-run summary */}
      {lastResult && (
        <Card className="p-6 border-emerald-200 bg-emerald-50/50" data-testid="last-clear-summary">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            <h3 className="font-semibold text-slate-900">Latest maintenance run</h3>
          </div>
          <p className="text-sm text-slate-700 mb-3">
            Deleted <span className="font-mono font-bold">{lastResult.total_deleted}</span> transactional records.
            {lastResult.message}
          </p>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3">Maintenance History (last 20)</h3>
          <div className="text-xs">
            <div className="grid grid-cols-4 gap-2 font-semibold text-slate-500 uppercase tracking-wider border-b pb-2">
              <span>Date</span>
              <span>Action</span>
              <span>Performed By</span>
              <span className="text-right">Records Deleted</span>
            </div>
            {history.map((h, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 py-2 border-b border-slate-100 last:border-0">
                <span className="text-slate-700">{new Date(h.performed_at).toLocaleString()}</span>
                <span className="text-slate-700">{h.action}</span>
                <span className="text-slate-700">{h.performed_by_name} ({h.performed_by_email})</span>
                <span className="text-right font-mono font-semibold text-red-700">{h.total_deleted}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={(o) => { if (!o) { setShowConfirm(false); setConfirmText(""); } }}>
        <DialogContent data-testid="clear-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Confirm Data Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete all transaction data? <strong className="text-red-700">This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="border border-red-200 bg-red-50 rounded p-3">
              <div className="font-semibold text-red-800 mb-1">Impact:</div>
              <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                <li>{totalTxn} transactional records will be permanently deleted</li>
                <li>Masters, users, and numbering counters will be preserved</li>
                <li>Numbering sequence continues (existing inquiry numbers not reset)</li>
              </ul>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Type <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">DELETE ALL</span> to confirm
              </label>
              <Input
                data-testid="confirm-text-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE ALL"
                className="mt-1"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowConfirm(false); setConfirmText(""); }}
              disabled={clearing}
              data-testid="btn-cancel-clear"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={handleClearTransactions}
              disabled={clearing || confirmText !== "DELETE ALL"}
              data-testid="btn-confirm-clear"
            >
              {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
